
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import BusinessListCard from "../../Components/BusinessListCard";
import UserLayout from "../../DesignLayout/UserLayout";
import { FaArrowLeft, FaFilter } from "react-icons/fa";
import { FiX } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import baseURL from "../../config";
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { ShimmerBusinessCard } from '../../Components/ShimmerCard';

const CategoryBusinesses = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [businesses, setBusinesses] = useState<any[]>([]);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [selectedSub, setSelectedSub] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [categoryName, setCategoryName] = useState("");
  const [excelDataList, setExcelDataList] = useState<any[]>([]);
  const [selectedCard, setSelectedCard] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // 1️⃣ Fetch Subcategories
        const subRes = await axios.get(`${baseURL}/api/admin/subcategory/parent/${id}`);
        const subs = Array.isArray(subRes.data) ? subRes.data : (subRes.data.subcategories || []);
        setSubcategories(subs);

        // 2️⃣ Fetch Businesses
        const bizRes = await axios.get(`${baseURL}/api/user/business-by-category/${id}`);
        const bizs = bizRes.data.businesses || [];
        setBusinesses(bizs);

        // 3️⃣ Get Category Name
        let catName = "";
        if (bizRes.data.categoryName) {
          catName = bizRes.data.categoryName;
        } else if (subRes.data.categoryName) {
          catName = subRes.data.categoryName;
        } else if (bizs.length > 0 && bizs[0].category) {
          catName = bizs[0].category.name;
        } else if (subs.length > 0 && subs[0].parentCategory) {
          catName = subs[0].parentCategory.name;
        }

        if (catName) {
          setCategoryName(catName);
        } else {
          // Fallback: fetch all categories to find the name
          try {
            const allCat = await axios.get(`${baseURL}/api/admin/all-category`);
            if (Array.isArray(allCat.data)) {
              const found = allCat.data.find((c: any) => c._id === id);
              if (found) setCategoryName(found.name);
            }
          } catch(e) {
            console.error("Failed to fetch all categories for fallback name");
          }
        }

      } catch (err) {
        console.error("Error fetching category data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      window.scrollTo(0, 0);
      fetchData();
    }
  }, [id]);

  useEffect(() => {
    if (categoryName) {
      axios.get(`${baseURL}/api/user/excel-data/category/${categoryName}`)
        .then(res => {
          if(res.data.success) {
            setExcelDataList(res.data.data || []);
          }
        })
        .catch(err => console.log("Error fetching excel data", err));
    }
  }, [categoryName]);

  const [sortBy, setSortBy] = useState<string>("newest");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Filter and Sort businesses
  let filteredBusinesses = selectedSub
    ? businesses.filter((b: any) => b.subcategories && b.subcategories.some((s: any) => s._id === selectedSub))
    : [...businesses];

  const maxRating = filteredBusinesses.length > 0 
    ? Math.max(...filteredBusinesses.map((b: any) => b.rating || 0))
    : 0;

  // Apply Sorting
  filteredBusinesses = filteredBusinesses.sort((a, b) => {
    if (sortBy === "top_rated") {
      const aRating = a.rating || 0;
      const bRating = b.rating || 0;
      return bRating - aRating;
    }
    if (sortBy === "a_to_z") {
      return (a.businessName || "").localeCompare(b.businessName || "");
    }
    if (sortBy === "z_to_a") {
      return (b.businessName || "").localeCompare(a.businessName || "");
    }
    // Default: 'newest'
    return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
  });

  return (
    <UserLayout>
      <div className="min-h-screen bg-gray-50 pb-20 font-sans text-gray-800">

        {/* HEADER */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-white/80 backdrop-blur-md sticky top-0 z-20 px-3 sm:px-4 py-2.5 shadow-sm flex items-center justify-between"
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-700"
            >
              <FaArrowLeft size={14} />
            </button>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-bold text-[#3F87DF] truncate">
                {categoryName}
              </h1>
              <p className="text-[10px] sm:text-xs text-gray-500">{businesses.length} results found</p>
            </div>
          </div>

        </motion.div>

        {/* BADGE FILTERS (Horizontally Scrollable) */}
        <div
          className="bg-white px-3 sm:px-4 py-3 border-b border-gray-100 flex gap-2 overflow-x-auto whitespace-nowrap w-full hide-scroll"
          style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <style>{`
            .hide-scroll::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          {[
            { id: 'sort', label: 'Sort by' }
          ].map((badge) => (
            <button
              key={badge.id}
              onClick={() => {
                if (badge.id === 'sort') setShowFilterDropdown(!showFilterDropdown);
              }}
              className={`flex-shrink-0 flex items-center justify-center whitespace-nowrap px-4 py-1.5 border border-gray-300 rounded-lg text-sm font-semibold transition-colors
                ${badge.id === 'sort' && showFilterDropdown ? 'bg-violet-50 text-violet-700 border-violet-300' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              {badge.label}
            </button>
          ))}
        </div>

        {/* Sort Dropdown Panel */}
        <AnimatePresence>
          {showFilterDropdown && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white border-b border-gray-100 shadow-sm overflow-hidden"
            >
              <div className="px-4 py-2 flex flex-col gap-1">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Sort By</p>
                {[
                  { id: 'newest', label: 'Newest First' },
                  { id: 'top_rated', label: 'Top Rated' },
                  { id: 'a_to_z', label: 'Name (A to Z)' },
                  { id: 'z_to_a', label: 'Name (Z to A)' },
                ].map((option) => (
                  <button
                    key={option.id}
                    onClick={() => {
                      setSortBy(option.id);
                      setShowFilterDropdown(false);
                    }}
                    className={`text-left px-3 py-2 text-sm rounded transition-colors ${sortBy === option.id ? 'bg-violet-50 text-violet-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* EXCEL DATA CAROUSEL */}
        {excelDataList.length > 0 && (
          <div className="bg-white px-3 sm:px-4 py-4 pb-8 border-b border-gray-100">
            <h2 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-1.5 h-4 bg-[#3F87DF] rounded-full"></span>
              Important Information
            </h2>
            <div className="w-full">
              <style>{`
                .slick-dots li button:before {
                  font-size: 8px;
                  color: #3F87DF;
                }
                .slick-dots li.slick-active button:before {
                  color: #3F87DF;
                }
                .slick-dots {
                  bottom: -20px;
                }
              `}</style>
              <Slider 
                dots={true}
                infinite={excelDataList.length > 1}
                speed={500}
                slidesToShow={1}
                slidesToScroll={1}
                autoplay={excelDataList.length > 1}
                autoplaySpeed={4000}
                arrows={false}
              >
                {excelDataList.map((item) => (
                  <div key={item._id} className="outline-none px-1">
                    <div 
                      onClick={() => setSelectedCard(item)}
                      className="w-full bg-gradient-to-br from-blue-50 to-white border border-blue-100 p-5 rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer group"
                    >
                      <h3 className="font-bold text-[#3F87DF] text-sm sm:text-base mb-2 line-clamp-2 group-hover:text-blue-700 transition-colors">{item.title}</h3>
                      <p className="text-gray-600 text-xs sm:text-sm line-clamp-3 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        )}



        {/* CONTENT AREA */}
        <div className="w-full">
          {loading ? (
            <div className="space-y-3 pt-4 px-4">
              {[1, 2, 3].map((n) => (
                <ShimmerBusinessCard key={n} />
              ))}
            </div>
          ) : (
            <motion.div layout className="flex flex-col">
              <AnimatePresence mode='popLayout'>
                {filteredBusinesses.length > 0 ? (
                  filteredBusinesses.map((biz) => (
                    <motion.div
                      layout
                      key={biz._id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      <BusinessListCard business={biz} isTopRated={maxRating > 0 && (biz.rating || 0) === maxRating} />
                    </motion.div>
                  ))
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center justify-center pt-20 text-center"
                  >
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center text-3xl mb-4 text-gray-300">
                      🚫
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700">No businesses found</h3>
                    <p className="text-gray-500 text-sm mt-1">Try selecting a different subcategory.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </div>

      </div>

      {/* EXCEL DATA MODAL */}
      <AnimatePresence>
        {selectedCard && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedCard(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] z-[101]"
            >
              <div className="bg-[#3F87DF] p-4 flex justify-between items-start shrink-0">
                <h2 className="text-white font-bold text-base sm:text-lg pr-4">{selectedCard.title}</h2>
                <button onClick={() => setSelectedCard(null)} className="text-white/80 hover:text-white mt-0.5 shrink-0">
                  <FiX size={22} />
                </button>
              </div>
              <div className="p-4 sm:p-6 overflow-y-auto whitespace-pre-wrap text-gray-700 text-sm sm:text-base leading-relaxed">
                {selectedCard.description}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </UserLayout>
  );
};

export default CategoryBusinesses;
